import type {
  LoadBalancingScheduleEmploymentType,
  LoadBalancingScheduleShiftPreference as LoadBalancingScheduleShiftPreferenceType,
  LoadBalancingShiftPatternPreference as LoadBalancingShiftPatternPreferenceType,
} from '@common/graphql';

// The worker-term fields the Team Review surfaces — the shared selection of both the
// roster and single-worker queries (service-line-workers.ts / load-balancing-worker.ts).
// Declared structurally so the mapper serves both generated worker types and stays
// unit-testable with plain literals.
export type TeamReviewTermInput = {
  id: string;
  active: boolean;
  effectiveStartDate: string;
  effectiveEndDate?: string | null;
  employmentType: LoadBalancingScheduleEmploymentType;
  role?: string | null;
  homeUnit?: string | null;
  homeUnitId?: string | null;
  fteHours?: number | null;
  preferredTime: LoadBalancingScheduleShiftPreferenceType;
  shiftPatternPreference: LoadBalancingShiftPatternPreferenceType;
  chargeEligible: boolean;
};

export type TeamReviewWorkerInput = {
  id: string;
  name: string;
  terms: TeamReviewTermInput[];
};

// One forthcoming term (effective after `asOf`) shown in the worker drawer.
export type TeamReviewChange = {
  id: string;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
  role: string | null;
  fteHours: number | null;
  preferredTime: LoadBalancingScheduleShiftPreferenceType;
  shiftPatternPreference: LoadBalancingShiftPatternPreferenceType;
  chargeEligible: boolean;
  active: boolean;
};

// A flattened worker row: current-term values plus any forthcoming changes.
export type TeamReviewWorker = {
  id: string;
  name: string;
  role: string | null;
  unit: string | null;
  // Home unit uuid for the current term — the stable key the Unit filter matches on
  // (the `unit` name is display-only and not guaranteed unique). Null when unassigned.
  unitId: string | null;
  employmentType: LoadBalancingScheduleEmploymentType;
  shiftType: LoadBalancingScheduleShiftPreferenceType;
  shiftPattern: LoadBalancingShiftPatternPreferenceType;
  fteHours: number | null;
  chargeEligible: boolean;
  status: 'active' | 'inactive';
  upcomingChanges: TeamReviewChange[];
};

const SHIFT_PREFERENCE_LABEL: Record<
  LoadBalancingScheduleShiftPreferenceType,
  string
> = {
  DAY: 'Day',
  NIGHT: 'Night',
  EITHER: 'Flexible',
};

const EMPLOYMENT_LABEL: Record<LoadBalancingScheduleEmploymentType, string> = {
  CORE: 'Core',
  AGENCY: 'Agency',
};

const SHIFT_PATTERN_LABEL: Record<
  LoadBalancingShiftPatternPreferenceType,
  string
> = {
  CONSECUTIVE: 'Consecutive',
  NON_CONSECUTIVE: 'Non-consecutive',
  NO_PREFERENCE: 'No Shift Pattern',
};

export function shiftPreferenceLabel(
  preference: LoadBalancingScheduleShiftPreferenceType
): string {
  return SHIFT_PREFERENCE_LABEL[preference];
}

export function employmentLabel(
  employmentType: LoadBalancingScheduleEmploymentType
): string {
  return EMPLOYMENT_LABEL[employmentType];
}

export function shiftPatternLabel(
  shiftPattern: LoadBalancingShiftPatternPreferenceType
): string {
  return SHIFT_PATTERN_LABEL[shiftPattern];
}

// Display label for FTE hours over the scheduling period; em-dash when unknown (no
// scheduling period in scope or no FTE% on the term).
export function fteHoursLabel(fteHours: number | null): string {
  return fteHours === null ? '—' : `${Math.round(fteHours)}h`;
}

// The minimal term shape pickCurrentTerm / sortByStart actually read: the effective-date bounds,
// plus an optional id for a stable tiebreak. Looser than TeamReviewTermInput so leaner callers (the
// drag affordance's slim charge-eligibility terms, WORK-6528) reuse the same covering-term logic
// without carrying the full roster field set. `id` is optional — absent it, equal-start terms keep
// their input order.
export type TermCoverageInput = {
  effectiveStartDate: string;
  effectiveEndDate?: string | null;
  id?: string;
};

// Dates are ISO8601 'YYYY-MM-DD' strings, so lexical comparison is chronological.
function sortByStart<T extends TermCoverageInput>(terms: readonly T[]): T[] {
  return [...terms].sort(
    (a, b) =>
      a.effectiveStartDate.localeCompare(b.effectiveStartDate) ||
      (a.id ?? '').localeCompare(b.id ?? '')
  );
}

// The single term strictly in effect on `asOf` (start ≤ asOf ≤ end, or open-ended), or undefined
// when none covers `asOf` — NO fallback. Mirrors the server's `WorkerTerm.effective_on(date)` exactly
// (Constraints::Context#term_for), so callers that must match the assignment gate's verdict (the drag
// affordance's per-slot charge-eligibility, WORK-6528) fail closed on an uncovered date rather than
// over-promising from a neighbouring term.
export function findCoveringTerm<T extends TermCoverageInput>(
  terms: readonly T[],
  asOf: string
): T | undefined {
  // Compare date-only. Callers pass ISO8601 'YYYY-MM-DD', but guard against an ISO instant
  // ('2026-03-31T23:59:59Z') slipping in — the term bounds are date-only, so a lexical compare
  // against the full instant would break at the 'T' (which sorts after '-') and silently drop the
  // covering term.
  const on = asOf.slice(0, 10);
  return sortByStart(terms).find((term) => {
    const end = term.effectiveEndDate ?? null;
    return term.effectiveStartDate <= on && (end === null || end >= on);
  });
}

// The term in effect on `asOf` (start ≤ asOf ≤ end, or open-ended). When none covers
// `asOf`, prefer the most recent already-started term; if every term is future-dated,
// the earliest upcoming term (a not-yet-started hire still gets a row). Generic so callers
// keep the richer element type (e.g. the full worker-term fields) on the returned term.
export function pickCurrentTerm<T extends TermCoverageInput>(
  terms: readonly T[],
  asOf: string
): T | undefined {
  if (terms.length === 0) return undefined;

  const covering = findCoveringTerm(terms, asOf);
  if (covering) return covering;

  const sorted = sortByStart(terms);
  const started = sorted.filter((term) => term.effectiveStartDate <= asOf);
  return started.length > 0 ? started[started.length - 1] : sorted[0];
}

function toChange(term: TeamReviewTermInput): TeamReviewChange {
  return {
    id: term.id,
    effectiveStartDate: term.effectiveStartDate,
    effectiveEndDate: term.effectiveEndDate ?? null,
    role: term.role ?? null,
    fteHours: term.fteHours ?? null,
    preferredTime: term.preferredTime,
    shiftPatternPreference: term.shiftPatternPreference,
    chargeEligible: term.chargeEligible,
    active: term.active,
  };
}

// Flattens a worker's term history into a single review row: the current term drives the
// displayed values; every later, future-dated term becomes an "upcoming change".
export function toTeamReviewWorker(
  worker: TeamReviewWorkerInput,
  asOf: string
): TeamReviewWorker {
  const current = pickCurrentTerm(worker.terms, asOf);
  const upcomingChanges = sortByStart(worker.terms)
    .filter((term) => term.effectiveStartDate > asOf && term.id !== current?.id)
    .map(toChange);

  return {
    id: worker.id,
    name: worker.name,
    role: current?.role ?? null,
    unit: current?.homeUnit ?? null,
    unitId: current?.homeUnitId ?? null,
    employmentType: current?.employmentType ?? 'CORE',
    shiftType: current?.preferredTime ?? 'EITHER',
    shiftPattern: current?.shiftPatternPreference ?? 'NO_PREFERENCE',
    fteHours: current?.fteHours ?? null,
    chargeEligible: current?.chargeEligible ?? false,
    status: current?.active ? 'active' : 'inactive',
    upcomingChanges,
  };
}
